/**
 * Admin editor for loyalty tier configuration:
 * - Name (slug)
 * - Lifetime-spend threshold ($)
 * - Point multiplier
 * - Per-tier perks list. Each perk: label, type (info | percent | fixed), value.
 *
 * Saves via PUT /api/loyalty/tier-config (admin-only, full replace).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, FloppyDisk, Crown } from "@phosphor-icons/react";

const newPerk = () => ({
  id: crypto.randomUUID ? crypto.randomUUID() : `p_${Math.random().toString(36).slice(2)}`,
  label: "",
  type: "info",
  value: 0,
  cooldown_days: 0,
});

const LoyaltyTiersCard = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/loyalty/tier-config`);
      setTiers((r.data?.tiers || []).map(t => ({
        ...t,
        perks: (t.perks || []).map(p => ({ id: p.id, label: p.label || "", type: p.type || "info", value: p.value ?? 0, cooldown_days: p.cooldown_days ?? 0 })),
      })));
    } catch (e) {
      toast.error("Failed to load tiers: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setTier = (idx, patch) => setTiers(prev => prev.map((t, i) => i === idx ? { ...t, ...patch } : t));
  const addPerk = (idx) => setTier(idx, { perks: [...tiers[idx].perks, newPerk()] });
  const updatePerk = (idx, pidx, patch) => setTier(idx, {
    perks: tiers[idx].perks.map((p, i) => i === pidx ? { ...p, ...patch } : p),
  });
  const removePerk = (idx, pidx) => setTier(idx, {
    perks: tiers[idx].perks.filter((_, i) => i !== pidx),
  });

  const save = async () => {
    // Sanity check: every tier must have a name, sorted thresholds make sense.
    for (const t of tiers) {
      if (!t.name?.trim()) return toast.error("Every tier needs a name");
      for (const p of t.perks) {
        if (!p.label?.trim()) return toast.error(`Empty perk label in ${t.name}`);
      }
    }
    setSaving(true);
    try {
      await axios.put(`${API}/loyalty/tier-config`, {
        tiers: tiers.map(t => ({
          name: t.name.trim().toLowerCase(),
          threshold: parseFloat(t.threshold) || 0,
          multiplier: parseFloat(t.multiplier) || 1,
          perks: t.perks.map(p => ({
            id: p.id,
            label: p.label.trim(),
            type: p.type || "info",
            value: parseFloat(p.value) || 0,
            cooldown_days: Math.max(0, parseInt(p.cooldown_days, 10) || 0),
          })),
        })),
      });
      toast.success("Loyalty tiers saved");
      await load();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="loyalty-tiers-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Crown size={20} weight="fill" className="text-accent" />
          Loyalty Tiers
        </CardTitle>
        <CardDescription>
          Edit the spend thresholds, point multipliers, and perks for each tier. Perks marked
          as <strong>percent</strong> or <strong>fixed</strong> become one-tap redeemable in
          the Pay wizard&apos;s Loyalty step. <strong>info</strong> perks are display-only.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!loading && tiers.map((t, idx) => (
          <div key={t.name + idx} className="rounded-lg border border-border p-4 space-y-3" data-testid={`tier-${t.name}`}>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Name</Label>
                <Input
                  value={t.name}
                  onChange={(e) => setTier(idx, { name: e.target.value })}
                  className="capitalize"
                  data-testid={`tier-${idx}-name`}
                />
              </div>
              <div className="space-y-1">
                <Label>Threshold ($ spent)</Label>
                <Input
                  type="number" min={0} step={50}
                  value={t.threshold}
                  onChange={(e) => setTier(idx, { threshold: e.target.value })}
                  className="font-mono"
                  data-testid={`tier-${idx}-threshold`}
                />
              </div>
              <div className="space-y-1">
                <Label>Point multiplier</Label>
                <Input
                  type="number" min={0} step={0.05}
                  value={t.multiplier}
                  onChange={(e) => setTier(idx, { multiplier: e.target.value })}
                  className="font-mono"
                  data-testid={`tier-${idx}-multiplier`}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Perks</Label>
                <Button size="sm" variant="outline" onClick={() => addPerk(idx)} className="h-8 gap-1" data-testid={`tier-${idx}-add-perk`}>
                  <Plus size={14} /> Add perk
                </Button>
              </div>
              {t.perks.length === 0 && <p className="text-xs text-muted-foreground">No perks yet.</p>}
              {t.perks.map((p, pidx) => (
                <div key={p.id} className="grid grid-cols-[1fr_130px_100px_110px_36px] gap-2 items-end" data-testid={`tier-${idx}-perk-${pidx}`}>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Label</Label>
                    <Input value={p.label} onChange={(e) => updatePerk(idx, pidx, { label: e.target.value })} placeholder="e.g. Free dessert" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <Select value={p.type} onValueChange={(v) => updatePerk(idx, pidx, { type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="info">Info only</SelectItem>
                        <SelectItem value="percent">% off</SelectItem>
                        <SelectItem value="fixed">$ off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      {p.type === "percent" ? "Percent" : p.type === "fixed" ? "Dollars" : "—"}
                    </Label>
                    <Input
                      type="number" min={0} step={p.type === "percent" ? 1 : 0.5}
                      value={p.value}
                      disabled={p.type === "info"}
                      onChange={(e) => updatePerk(idx, pidx, { value: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground" title="Days before same member can reuse this perk (0 = no cooldown)">
                      Cooldown (days)
                    </Label>
                    <Input
                      type="number" min={0} step={1}
                      value={p.cooldown_days ?? 0}
                      disabled={p.type === "info"}
                      onChange={(e) => updatePerk(idx, pidx, { cooldown_days: e.target.value })}
                      placeholder="0"
                      className="font-mono"
                      data-testid={`tier-${idx}-perk-${pidx}-cooldown`}
                    />
                  </div>
                  <Button variant="ghost" size="sm" className="h-9 w-9 p-0" onClick={() => removePerk(idx, pidx)}>
                    <Trash size={16} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving || loading} className="gap-2" data-testid="save-tiers-btn">
            <FloppyDisk size={18} weight="bold" />
            {saving ? "Saving…" : "Save Tiers"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default LoyaltyTiersCard;
